import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import type { ProfileOutletContext } from "../ProfileLayout";
import { createPayment, getPaymentStatus, listPayments } from "../../services/payments";
import PaymentHistory, { PaymentHistoryItem } from "../../components/PaymentHistory";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";

const formatCurrency = (value: number | null | undefined) => {
  const amount = typeof value === "number" ? value : Number(value ?? 0);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number.isFinite(amount) ? amount : 0);
};

const BillingPage: React.FC = () => {
  const { subscription, user, refreshUser } = useOutletContext<ProfileOutletContext>();
  const [amount, setAmount] = useState<string>("5");
  const [isPaying, setIsPaying] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [history, setHistory] = useState<PaymentHistoryItem[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const usagePercent = useMemo(() => {
    if (!subscription || subscription.limit === 0) {
      return 0;
    }

    return Math.min(100, Math.round((subscription.usage / subscription.limit) * 100));
  }, [subscription]);

  const limitLabel = useMemo(() => {
    if (!subscription) {
      return "—";
    }

    if (!Number.isFinite(subscription.limit) || subscription.limit >= Number.MAX_SAFE_INTEGER) {
      return "∞";
    }

    return subscription.limit;
  }, [subscription]);

  const loadHistory = useCallback(async () => {
    try {
      setIsHistoryLoading(true);
      const payments = await listPayments();
      setHistory(
        payments.data.map((item) => ({
          ...item,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }))
      );
    } catch (error) {
      console.error("Failed to load payment history", error);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load history implicitly if needed, or when tab is active. 
    // Simplified: always load initially or on mount relative to logic.
    // But actually, let's keep it tied to tab switching if possible, OR just load on mount.
    // The original code passed 'activeTab' dependency. 
    // Here with Shadcn Tabs, we might want to just load it when the component mounts if it's cheap, or lazily.
    // Let's stick to simple mount for now.
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const urlPaymentId = searchParams.get("paymentId");
    const storedPaymentId = sessionStorage.getItem("pendingPaymentId");
    const paymentId = urlPaymentId ?? storedPaymentId;

    if (!paymentId) {
      return;
    }

    const checkStatus = async () => {
      try {
        setStatusMessage("Checking payment status...");
        const status = await getPaymentStatus(paymentId);

        if (status.status === "succeeded" && status.processed) {
          setStatusMessage("Payment successful. Balance updated.");
          await refreshUser();
          await loadHistory();
          sessionStorage.removeItem("pendingPaymentId");
        } else if (status.status === "pending") {
          setStatusMessage("Payment is processing. Refresh the page later.");
        } else {
          setStatusMessage(`Payment status: ${status.status}`);
        }
      } catch (error: any) {
        setStatusMessage(error?.response?.data?.message || error?.message || "Failed to get payment status.");
      } finally {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("paymentId");
        setSearchParams(nextParams, { replace: true });
      }
    };

    void checkStatus();
  }, [loadHistory, refreshUser, searchParams, setSearchParams]);

  const handleCreatePayment = async () => {
    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount) || numericAmount < 5) {
      setStatusMessage("Minimum payment amount is $5.");
      return;
    }

    setIsPaying(true);
    setStatusMessage(null);

    try {
      const result = await createPayment(numericAmount, "Top up balance");
      sessionStorage.setItem("pendingPaymentId", result.paymentId);
      window.location.href = result.confirmationUrl;
    } catch (error: any) {
      setStatusMessage(error?.response?.data?.message || error?.message || "Failed to create payment.");
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col gap-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Billing</h2>
          <p className="text-muted-foreground">
            Track subscription status, limits, and payment methods.
          </p>
          {statusMessage && (
            <div className="mt-4 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              {statusMessage}
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="plan" className="space-y-4">
        <TabsList>
          <TabsTrigger value="plan">Current Plan</TabsTrigger>
          <TabsTrigger value="history">Payment History</TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Current Plan</CardTitle>
                  <CardDescription>Manage your subscription and limits.</CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Balance</p>
                  <p className="text-2xl font-bold">{formatCurrency(user.balance)}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium">{subscription?.planLabel ?? "Start"}</p>
                  <span className="text-sm text-muted-foreground">{subscription?.planDescription}</span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Usage</span>
                    <span className="text-muted-foreground">{subscription ? `${subscription.usage} / ${limitLabel}` : "—"}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-secondary">
                    <div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${usagePercent}%` }}></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top Up Balance</CardTitle>
              <CardDescription>Funds will appear automatically after confirmation.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="amount">Amount (USD)</Label>
                  <Input
                    type="number"
                    id="amount"
                    placeholder="5"
                    min={5}
                    step={1}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <Button
                  onClick={handleCreatePayment}
                  disabled={isPaying}
                >
                  {isPaying ? "Processing..." : "Top Up Balance"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <PaymentHistory items={history} isLoading={isHistoryLoading} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingPage;
